import { useMemo, useState } from 'react';
import { useVault } from '../lib/vault';
import { CYCLE_LABEL, annualMoney, formatMoney } from '@shared/money';
import { plural } from '@shared/text';
import { PAYMENT_LABEL, PORTAL_LABEL, type InvoicePortal } from '@shared/types';
import { ItemIcon } from '../components/ItemIcon';

/**
 * Alle Zugaenge, bei denen ein Link zum Rechnungsbereich hinterlegt ist -
 * damit die Buchhaltung nicht jedes Mal von vorn sucht.
 */
export function InvoicesPage(): JSX.Element {
  const { items, users } = useVault();
  const [query, setQuery] = useState('');

  const gruppen = useMemo(() => {
    const q = query.trim().toLowerCase();
    const relevant = items
      .filter((i) => i.hasCost && i.status !== 'cancelled')
      .filter((i) => !q || `${i.title} ${i.category}`.toLowerCase().includes(q));

    const mitLink = relevant.filter((i) => i.invoiceUrl.trim());
    const ohneLink = relevant.filter((i) => !i.invoiceUrl.trim());

    const nachPortal = new Map<InvoicePortal, typeof mitLink>();
    for (const item of mitLink) {
      const key = item.invoicePortal;
      nachPortal.set(key, [...(nachPortal.get(key) ?? []), item]);
    }
    for (const liste of nachPortal.values()) {
      liste.sort((a, b) => a.title.localeCompare(b.title, 'de'));
    }

    return {
      mitLink,
      ohneLink: ohneLink.sort(
        (a, b) => annualMoney(b).grossCents - annualMoney(a).grossCents,
      ),
      nachPortal: [...nachPortal.entries()].sort((a, b) => b[1].length - a[1].length),
    };
  }, [items, query]);

  const nameOf = (uid: string | null): string => users.find((u) => u.uid === uid)?.name ?? '–';

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Rechnungen</h1>
        </div>
        <input
          style={{ maxWidth: 220 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Suchen …"
          aria-label="Suchen"
        />
      </div>

      {gruppen.mitLink.length === 0 && gruppen.ohneLink.length === 0 ? (
        <div className="empty">Keine kostenpflichtigen Zugänge erfasst.</div>
      ) : null}

      {gruppen.nachPortal.map(([portal, liste]) => (
        <div className="card" key={portal}>
          <div className="card-title">
            <h2>{PORTAL_LABEL[portal]}</h2>
            <span className="small muted">{plural(liste.length, 'Anbieter', 'Anbieter')}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Dienst</th>
                <th className="col-optional">Zahlung</th>
                <th className="num col-optional">aufs Jahr</th>
                <th className="num" style={{ width: 130 }} />
              </tr>
            </thead>
            <tbody>
              {liste.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="row-tight">
                      <ItemIcon item={item} size={22} />
                      <div>
                        <strong>{item.title}</strong>
                        <div className="small muted">
                          {[item.category, `verantwortlich: ${nameOf(item.ownerUid)}`]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="small muted col-optional">
                    {PAYMENT_LABEL[item.paymentMethod]}
                    {item.paymentDetail ? ` · ${item.paymentDetail}` : ''}
                    <div className="small muted">{CYCLE_LABEL[item.cycle]}</div>
                  </td>
                  <td className="num mono col-optional">
                    {formatMoney(annualMoney(item).grossCents)}
                  </td>
                  <td className="num">
                    <a
                      className="btn btn-sm btn-link-out"
                      href={item.invoiceUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Rechnungen{'\u00a0↗'}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {gruppen.ohneLink.length > 0 ? (
        <div className="card">
          <div className="card-title">
            <h2>Ohne hinterlegten Link</h2>
            <span className="small muted">
              {plural(gruppen.ohneLink.length, 'Eintrag', 'Einträge')}
            </span>
          </div>
          <p className="small muted" style={{ marginBottom: 12 }}>
            Teuerste zuerst – dort lohnt sich das Nachtragen am meisten. Den Link findest du beim
            Anbieter meist unter „Billing", „Rechnungen" oder im Kontobereich.
          </p>
          <table>
            <tbody>
              {gruppen.ohneLink.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="row-tight">
                      <ItemIcon item={item} size={22} />
                      <div>
                        <strong>{item.title}</strong>
                        <div className="small muted">
                          {item.invoicePortal === 'none'
                            ? 'Rechnungsweg nicht hinterlegt'
                            : PORTAL_LABEL[item.invoicePortal]}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="num mono small muted">
                    {formatMoney(annualMoney(item).grossCents)}
                  </td>
                  <td className="num">
                    {item.url ? (
                      <a
                        className="small"
                        href={item.url}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        zum Anbieter{'\u00a0↗'}
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
